import type * as Core from "@zeller-public/payments-sdk-core";
import type { z } from "zod";

import * as transport from "../../../transport";
import type { PurchaseResponseSchema } from "../terminal-purchase/schemas";
import type { RefundResponseSchema } from "../terminal-refund/shared/schemas";
import { ProtocolErrorType_fromPosConnectorResponse } from "./declined-response/protocol-error-type-from-pos-connector-response";
import { ProtocolTransaction_fromPosConnectorResponse } from "./protocol-transaction-from-pos-connector-response";

////////////////////////////////////////////////////////////////////////////////

export function DeclineResponse_fromPurchasePosConnectorResponse(options: {
	data: z.infer<typeof PurchaseResponseSchema>["data"];
	reference: string | undefined;
}): Extract<
	Core.Protocol.Response,
	{ $type: "Result" | "ResultError"; query?: "Terminal_purchase" }
> {
	const resolve = transport.Resolve<"Terminal_purchase">();

	const value = getDeclinedValue(options);
	if (typeof value === "string") {
		return resolve.$Error(value);
	}

	return resolve.$Ok({ ...value, $type: "Declined", type: "PURCHASE" });
}

export function DeclineResponse_fromRefundPosConnectorResponse(options: {
	data: z.infer<typeof RefundResponseSchema>["data"];
	reference: string | undefined;
}): Extract<
	Core.Protocol.Response,
	{ $type: "Result" | "ResultError"; query?: "Terminal_refund" }
> {
	const resolve = transport.Resolve<"Terminal_refund">();

	const value = getDeclinedValue(options);
	if (typeof value === "string") {
		return resolve.$Error(value);
	}

	return resolve.$Ok({ ...value, $type: "Declined", type: "REFUND" });
}

////////////////////////////////////////////////////////////////////////////////

function getDeclinedValue(options: {
	data: z.infer<typeof PurchaseResponseSchema>["data"];
	reference: string | undefined;
}): NonNullable<
	| (ReturnType<typeof ProtocolTransaction_fromPosConnectorResponse> & {
			status: "DECLINED";
			externalReference: string | undefined;
	  })
	| ReturnType<typeof ProtocolErrorType_fromPosConnectorResponse>
> {
	const { data, reference } = options;

	const type = ProtocolErrorType_fromPosConnectorResponse(data);
	if (type) {
		return type;
	}

	return {
		...ProtocolTransaction_fromPosConnectorResponse(data),
		status: "DECLINED",
		externalReference: reference,
	};
}
